import { useState } from 'react';
import { api, ApiError, type OrderRequest } from '../api';
import { formatMoney } from '../cart';
import type { T } from '../i18n';

declare global {
  interface Window {
    Razorpay?: new (options: Record<string, unknown>) => { open: () => void };
    Stripe?: (key: string) => any;
  }
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) return resolve();
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Payment provider could not be loaded'));
    document.head.appendChild(s);
  });
}

type Props = { token: string; request: OrderRequest; currency: string | null; t: T; onPaid: () => void };

// The browser only starts the provider's checkout. Whether the order is paid is decided
// server-side from the provider's signed webhook; the status view picks that up by polling.
export function PayOnline({ token, request, currency, t, onPaid }: Props) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [stripeReady, setStripeReady] = useState<{ stripe: any; elements: any } | null>(null);

  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      const intent = await api.paymentIntent(token, request.id);
      if (intent.provider === 'razorpay' && intent.checkout) {
        await loadScript('https://checkout.razorpay.com/v1/checkout.js');
        new window.Razorpay!({ ...intent.checkout, handler: () => onPaid() }).open();
      } else if (intent.provider === 'stripe' && intent.client_secret && intent.checkout?.publishable_key) {
        await loadScript('https://js.stripe.com/v3/');
        const stripe = window.Stripe!(String(intent.checkout.publishable_key));
        const elements = stripe.elements({ clientSecret: intent.client_secret });
        setStripeReady({ stripe, elements });
        setTimeout(() => elements.create('payment').mount('#stripe-payment'), 0);
      } else {
        throw new Error('Online payment is not configured');
      }
    } catch (e) {
      setError(e instanceof ApiError || e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const confirmStripe = async () => {
    if (!stripeReady) return;
    setBusy(true);
    const { error: err } = await stripeReady.stripe.confirmPayment({ elements: stripeReady.elements, redirect: 'if_required' });
    setBusy(false);
    if (err) setError(err.message);
    else onPaid();
  };

  const total = request.confirmed_totals?.total ?? 0;
  return (
    <div>
      {!stripeReady && <button className="btn" disabled={busy} onClick={start}>{t('payNow', { price: formatMoney(total, currency) })}</button>}
      {stripeReady && (
        <>
          <div id="stripe-payment" style={{ margin: '12px 0' }} />
          <button className="btn" disabled={busy} onClick={confirmStripe}>{t('payNow', { price: formatMoney(total, currency) })}</button>
        </>
      )}
      {error && <p className="error" role="alert">{error}</p>}
    </div>
  );
}
